import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Chip,
  Divider,
  CircularProgress,
  Alert
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import LabelIcon from '@mui/icons-material/Label';
import stripeService from '../../services/stripeService';

const SubscriptionPlans = () => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPlan, setCurrentPlan] = useState(null);

  // Format currency for display
  const formatCurrency = (amount) => {
    if (amount === undefined || amount === null) return 'Free';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount);
  };

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        setLoading(true);
        
        // Get admin viewable plans
        const response = await stripeService.getAdminViewablePlans();
        
        if (response.success && Array.isArray(response.data)) {
          // Filter out inactive plans
          const activePlans = response.data.filter(plan => plan.active !== false);
          setPlans(activePlans);
          
          // Try to get current subscription
          try {
            const subscriptionResponse = await stripeService.getTenantSubscription();
            if (subscriptionResponse.success && subscriptionResponse.data) {
              setCurrentPlan(subscriptionResponse.data.planId);
            }
          } catch (subError) {
            console.error('Error fetching current subscription:', subError);
            // Non-fatal error, continue without current plan info
          }
        } else {
          throw new Error('Failed to retrieve subscription plans');
        }
      } catch (err) {
        console.error('Error loading subscription plans:', err);
        setError('Error loading plans. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchPlans();
  }, []);

  const handlePlanSelect = (plan) => {
    // Check if this is the current plan
    if (currentPlan === plan.planId) {
      // Already subscribed to this plan
      return;
    }
    
    // Navigate to checkout with plan details
    navigate('/admin/subscription/checkout', {
      state: {
        planId: plan.id,
        planName: plan.name,
        price: plan.price,
        interval: plan.interval || 'month',
        description: plan.description,
        features: plan.features
      }
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ my: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box sx={{ mt: 4 }}>
      <Grid container spacing={3}>
        {plans.map((plan) => {
          const isCurrentPlan = currentPlan === plan.planId;
          const isRecommended = plan.recommended;
          
          return (
            <Grid item xs={12} sm={6} md={4} key={plan.id}>
              <Card 
                raised={isRecommended} 
                sx={{ 
                  position: 'relative', 
                  height: '100%', 
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'transform 0.3s',
                  '&:hover': {
                    transform: 'translateY(-5px)'
                  },
                  ...(isRecommended && {
                    border: '2px solid #3f51b5',
                    boxShadow: '0 8px 16px rgba(63, 81, 181, 0.2)'
                  })
                }}
              >
                {isRecommended && (
                  <Chip
                    icon={<LabelIcon />}
                    label="Recommended"
                    color="primary"
                    sx={{
                      position: 'absolute',
                      top: -12,
                      right: 20,
                      fontWeight: 'bold'
                    }}
                  />
                )}
                
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography variant="h5" gutterBottom component="div" sx={{ fontWeight: 'bold' }}>
                    {plan.name}
                  </Typography>
                  
                  <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 2 }}>
                    <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                      {formatCurrency(plan.price)}
                    </Typography>
                    <Typography variant="subtitle1" sx={{ ml: 1, color: 'text.secondary' }}>
                      /{plan.interval || 'month'}
                    </Typography>
                  </Box>
                  
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {plan.description}
                  </Typography>
                  
                  <Divider sx={{ my: 2 }} />
                  
                  <Typography variant="subtitle2" gutterBottom>
                    Features:
                  </Typography>
                  
                  {plan.features && Array.isArray(plan.features) ? (
                    plan.features.map((feature, idx) => (
                      <Box key={idx} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <CheckIcon color="success" fontSize="small" sx={{ mr: 1 }} />
                        <Typography variant="body2">
                          {feature}
                        </Typography>
                      </Box>
                    ))
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Feature list not available
                    </Typography>
                  )}
                </CardContent>
                
                <CardActions sx={{ p: 2, pt: 0 }}>
                  {isCurrentPlan ? (
                    <Button 
                      variant="outlined" 
                      color="success" 
                      fullWidth 
                      disabled
                    >
                      Current Plan
                    </Button>
                  ) : (
                    <Button 
                      variant={isRecommended ? "contained" : "outlined"} 
                      color="primary" 
                      fullWidth
                      onClick={() => handlePlanSelect(plan)}
                    >
                      Subscribe
                    </Button>
                  )}
                </CardActions>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

export default SubscriptionPlans;